import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from './language-context';
import { useSession } from './session-context';
import { useTheme } from './theme-context';
import type { ThemePref } from './theme-context';
import { useLayer } from './layer-stack';
import { NavIcon } from './nav-icon';
import type { NavIconName } from './nav-icon';
import { canAccess, navItemFor } from './nav-items';
import type { SectionId } from './nav-items';
import { AccountSwitcher } from './account-switcher';
import { SignOutButton } from './sign-out-button';
import { initialsOf } from './initials-of';
import { appEnvironment, envPillFor } from './app-build';
import { localiseDigits } from '@bookingdei/i18n/format-number';
import type { MessageKey } from '@bookingdei/i18n/message-key';
import { phonePlan } from '../theme/phone-plan';
import { formatPhone } from '@bookingdei/i18n/phone';

// CB-17 account menu (design D:342-421, script D:8741-8872): identity header
// (initials, name, "You", role chip, phone), jump rows, preferences (theme +
// language), the M32 "Continue as…" switcher for multi-role accounts, sign-out.
// Omitted from the design, each for a recorded reason:
//   - Change password — sign-in is OTP-only (DR-34);
//   - Preview as role — no DR covers it (gap analysis §7.3 item 32 open) and
//     custom roles / takeover are rejected (DR-43); a read-only preview would need
//     a server-side read-only token to be honest, which the contract lacks;
//   - Seat switch + the "2-factor On · SMS" meta — demo data (GAP §4).

interface JumpRow {
  icon: NavIconName;
  label: MessageKey;
  sub: MessageKey;
  section: SectionId;
  query?: string;
  tag?: number;
}

export function AccountMenu({
  open,
  onClose,
  unread,
}: {
  open: boolean;
  onClose: () => void;
  unread: number;
}) {
  const { t, lang, setLang } = useLanguage();
  const { pref, setPref } = useTheme();
  const { user, grants } = useSession();
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);
  useLayer(open, onClose, { blocking: false });

  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  if (!open || typeof document === 'undefined') return null;
  const env = envPillFor(appEnvironment);

  const rows: JumpRow[] = [
    { icon: 'shield', label: 'admin.shell.acct.team', sub: 'admin.shell.acct.team_sub', section: 'roles' },
    { icon: 'clipboard', label: 'admin.shell.acct.activity', sub: 'admin.shell.acct.activity_sub', section: 'roles', query: 'tab=audit' },
    { icon: 'bell', label: 'admin.shell.acct.notif', sub: 'admin.shell.acct.notif_sub', section: 'notifications', query: 'tab=alerts', tag: unread },
  ];
  const visibleRows = rows.filter((r) => canAccess(grants, r.section));
  const roleLabel = grants.length ? grants.map((g) => t(`admin.grant.${g}` as MessageKey)).join(' · ') : t('admin.grant.none');
  const themeOpts: Array<{ v: ThemePref; label: MessageKey }> = [
    { v: 'light', label: 'admin.shell.acct.light' },
    { v: 'dark', label: 'admin.shell.acct.dark' },
  ];
  const go = (r: JumpRow) => {
    onClose();
    navigate(`${navItemFor(r.section).to}${r.query ? `?${r.query}` : ''}`);
  };

  return createPortal(
    <div className="acct-layer">
      <div className="acct__backdrop" onClick={onClose} aria-hidden="true" data-backdrop />
      <div ref={panelRef} tabIndex={-1} className="acct" role="dialog" aria-label={t('admin.shell.account')}>
        <div className="acct__head">
          <span className="acct__avatar" aria-hidden="true">
            {initialsOf(user?.name)}
          </span>
          <div className="acct__id">
            <div className="acct__name-row">
              <span className="acct__name">{user?.name}</span>
              <span className="acct__you">{t('admin.shell.acct.you')}</span>
            </div>
            <div className="acct__role">{roleLabel}</div>
            {user?.phone && <div className="acct__phone">{formatPhone(user.phone, phonePlan)}</div>}
          </div>
        </div>
        {env && (
          <div className="acct__metas">
            <div>
              <div className="acct__meta-k">{t('admin.shell.acct.environment')}</div>
              <div className="acct__meta-v">
                {t(`admin.shell.env.${env}`)}
              </div>
            </div>
            <div>
              <div className="acct__meta-k">{t('admin.shell.acct.roles')}</div>
              <div className="acct__meta-v">{localiseDigits(lang, grants.length)}</div>
            </div>
          </div>
        )}
        <div className="acct__body">
          {visibleRows.map((r) => (
            <button key={r.label} type="button" className="acct__item" onClick={() => go(r)}>
              <span className="acct__item-icon">
                <NavIcon name={r.icon} size={15} />
              </span>
              <span className="acct__item-text">
                <span className="acct__item-label">{t(r.label)}</span>
                <span className="acct__item-sub">{t(r.sub)}</span>
              </span>
              {r.tag ? <span className="acct__item-tag">{localiseDigits(lang, r.tag)}</span> : null}
            </button>
          ))}

          <div className="acct__sec">{t('admin.shell.acct.prefs')}</div>
          <div className="acct__prefs">
            <div className="acct__pref">
              <span className="acct__pref-label">{t('admin.shell.acct.appearance')}</span>
              <div className="acct__seg" role="group" aria-label={t('admin.shell.acct.appearance')}>
                {themeOpts.map((o) => (
                  <button key={o.v} type="button" className={pref === o.v ? 'is-active' : ''} aria-pressed={pref === o.v} onClick={() => setPref(o.v)}>
                    {t(o.label)}
                  </button>
                ))}
              </div>
            </div>
            <div className="acct__pref">
              <span className="acct__pref-label">{t('app.language')}</span>
              <div className="acct__seg" role="group" aria-label={t('app.language')}>
                <button type="button" className={lang === 'en' ? 'is-active' : ''} aria-pressed={lang === 'en'} onClick={() => setLang('en')}>
                  {t('lang.en_short')}
                </button>
                <button type="button" className={lang === 'bn' ? 'is-active' : ''} aria-pressed={lang === 'bn'} onClick={() => setLang('bn')}>
                  {t('lang.bn_short')}
                </button>
              </div>
            </div>
          </div>
          <div className="acct__switch">
            <AccountSwitcher />
          </div>
        </div>
        <SignOutButton className="acct__signout" />
      </div>
    </div>,
    document.body,
  );
}
